const API_BASE = import.meta.env.VITE_API_BASE || '/api';
let jwt = null;
export function setAuthToken(token) {
    jwt = token;
    try {
        sessionStorage.setItem('pm:jwt', token);
    }
    catch {
        /* ignore */
    }
}
export function getAuthToken() {
    if (jwt)
        return jwt;
    try {
        jwt = sessionStorage.getItem('pm:jwt');
    }
    catch {
        jwt = null;
    }
    return jwt;
}
export function setAuthTokenWeb(token) {
    jwt = token;
    try {
        localStorage.setItem('pm:jwt:web', token);
    }
    catch {
        /* ignore */
    }
}
export function getAuthTokenWeb() {
    try {
        return localStorage.getItem('pm:jwt:web');
    }
    catch {
        return null;
    }
}
export function clearAuthTokenWeb() {
    jwt = null;
    try {
        localStorage.removeItem('pm:jwt:web');
    }
    catch {
        /* ignore */
    }
}
/** Полный выход: снимаем и session-token (sessionStorage), и долгоживущий
 *  web-token (localStorage) — иначе bootstrap в shell/auth.tsx подхватит
 *  оставшийся токен и залогинит обратно. */
export function clearAuthTokens() {
    jwt = null;
    try {
        sessionStorage.removeItem('pm:jwt');
    }
    catch {
        /* ignore */
    }
    try {
        localStorage.removeItem('pm:jwt:web');
    }
    catch {
        /* ignore */
    }
}
// URL для скачивания полноразмерного PNG (lossless). <a download> не шлёт
// Authorization-заголовок, поэтому JWT передаём query-параметром ?token=.
export function generationDownloadUrl(genId) {
    const token = getAuthToken() ?? getAuthTokenWeb() ?? '';
    return `${API_BASE}/v1/webapp/generations/${genId}/download?token=${encodeURIComponent(token)}`;
}
// URL для скачивания zip браузерного расширения (только админам, гейт на бэке).
// <a download> не шлёт Authorization — JWT через ?token=, как в download выше.
export function extensionZipUrl() {
    const token = getAuthToken() ?? getAuthTokenWeb() ?? '';
    return `${API_BASE}/v1/webapp/extension.zip?token=${encodeURIComponent(token)}`;
}
/** Запустить скачивание файла по url. В НАТИВНОМ Telegram `<a href download>` не
 *  работает — встроенный webview не качает файлы (клик «проваливается»). Идём
 *  через TG SDK: `downloadFile` (нативный диалог сохранения, Bot API 8.0+) →
 *  `openLink` (внешний браузер; `Content-Disposition: attachment` форсит
 *  загрузку) → в обычном вебе программный `<a download>` (работает для
 *  same-origin url). */
function triggerDownload(url, fileName) {
    const tg = window.Telegram?.WebApp;
    const inTg = Boolean(tg?.initData);
    if (inTg && typeof tg?.downloadFile === 'function') {
        tg.downloadFile({ url, file_name: fileName });
        return;
    }
    if (inTg && typeof tg?.openLink === 'function') {
        tg.openLink(url);
        return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
}
/** Скачать PNG-генерацию из Mini App (см. triggerDownload — почему не сырой <a>). */
export function downloadGeneration(genId, fileName = `photomaker-${genId}.png`) {
    triggerDownload(generationDownloadUrl(genId), fileName);
}
/** Скачать видео-генерацию из Mini App. Та же проблема Telegram-webview, что и с
 *  PNG (см. triggerDownload): сырой `<a href download>` «проваливается».
 *  video_url — уже публичный mp4 с нашего домена (неугадываемый токен), auth не
 *  нужен. */
export function downloadVideo(videoUrl, fileName) {
    // Эндпоинт отдаёт mp4 inline (чтобы <video> в ленте играл). Для СКАЧИВАНИЯ
    // просим attachment-вариант (?dl=1) — иначе браузер/TG открывают плеер, а
    // `<a download>` для cross-origin URL игнорируется (см. serve_video_file).
    const sep = videoUrl.includes('?') ? '&' : '?';
    triggerDownload(`${videoUrl}${sep}dl=1`, fileName);
}
async function call(method, path, body) {
    const token = getAuthToken();
    const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`${method} ${path} → ${res.status}: ${text.slice(0, 200)}`);
    }
    if (res.status === 204)
        return undefined;
    return (await res.json());
}
export const api = {
    async authViaTelegram(initData) {
        return call('POST', '/v1/auth/telegram-webapp', { init_data: initData });
    },
    getMe: () => call('GET', '/v1/webapp/me'),
    getStats: () => call('GET', '/v1/webapp/me/stats'),
    listGenerations: (presetId, opts = {}) => {
        const q = new URLSearchParams({ limit: String(opts.limit ?? 100) });
        if (opts.offset)
            q.set('offset', String(opts.offset));
        if (presetId != null)
            q.set('preset_id', String(presetId));
        return call('GET', `/v1/webapp/generations?${q}`);
    },
    deleteGeneration: (id) => call('DELETE', `/v1/webapp/generations/${id}`),
    listStyles: () => call('GET', '/v1/webapp/styles'),
    setDefaultStyle: (id) => call('PATCH', `/v1/webapp/styles/${id}`, { is_default: true }),
    deleteStyle: (id) => call('DELETE', `/v1/webapp/styles/${id}`),
    listStoryboardPacks: () => call('GET', '/v1/storyboard/packs/me'),
    listPersonas: () => call('GET', '/v1/personas/me'),
    createPersona: (body) => call('POST', '/v1/personas', body),
    updatePersona: (id, body) => call('PATCH', `/v1/personas/${id}`, body),
    deletePersona: (id) => call('DELETE', `/v1/personas/${id}`),
    attachPackToPersona: (personaId, packId) => call('POST', `/v1/personas/${personaId}/attach-pack`, { pack_id: packId }),
    listLibrary: (kind) => {
        const q = kind ? `?kind=${kind}` : '';
        return call('GET', `/v1/library/me${q}`);
    },
    createLibraryItem: (body) => call('POST', '/v1/library', body),
    deleteLibraryItem: (id) => call('DELETE', `/v1/library/${id}`),
    useStoryboardCardAsRef: (cardId, prompt, opts = {}) => call('POST', `/v1/storyboard/cards/${cardId}/use-as-ref`, { prompt, model: opts.model, aspect_ratio: opts.aspect_ratio }),
    setLanguage: (language) => call('PATCH', '/v1/webapp/me', { language }),
    setPassword: (password) => call('POST', '/v1/web-auth/set-password', { password }),
    // ── Google Sign-In (GIS) ──
    googleLogin: (credential) => call('POST', '/v1/web-auth/google', { credential }),
    // Привязка Google к уже залогиненному аккаунту — auth-заголовок подставляет call() сам (getAuthToken()).
    linkGoogle: (credential) => call('POST', '/v1/web-auth/link/google', { credential }),
    // Привязка email+пароля к текущему аккаунту (для тех, кто вошёл через TG).
    // Дубль по email сливается в текущий аккаунт на бэкенде.
    linkEmail: (email, password) => call('POST', '/v1/web-auth/link/email', { email, password }),
    // Публичный конфиг сайта (те же данные, что publicClient.fetchSiteConfig, но
    // через общий call() — используется useGoogleSignIn для google_client_id).
    getSiteConfig: () => call('GET', '/v1/public/site-config'),
    getSupportMessages: () => call('GET', '/v1/webapp/support/messages'),
    sendSupportMessage: (content) => call('POST', '/v1/webapp/support/messages', { content }),
    getReferrals: () => call('GET', '/v1/webapp/referrals'),
    initTinkoffPayment: (plan, billing_cycle) => call('POST', '/v1/payments/tinkoff/init', { plan, billing_cycle }),
    initPaddleCheckout: (plan, billing_cycle) => call('POST', '/v1/payments/paddle/checkout', { plan, billing_cycle }),
    initUnitpayPayment: (plan, billing_cycle) => call('POST', '/v1/payments/unitpay/init', { plan, billing_cycle }),
    initRobokassaPayment: (plan, billing_cycle) => call('POST', '/v1/payments/robokassa/init', { plan, billing_cycle }),
    // ── Раздел «Идеи»: рекламные шаблоны (публично, без авторизации) ──
    getAdTemplates: (media = 'video') => call('GET', `/v1/public/ad-templates?media=${media}`),
    // ── Витрина «Примеры проектов»: showcase-канвасы (публично, read-only) ──
    getShowcase: (theme) => call('GET', `/v1/public/showcase${theme ? `?theme=${encodeURIComponent(theme)}` : ''}`),
    getPublicCanvas: (shareToken) => call('GET', `/v1/public/canvas/${encodeURIComponent(shareToken)}`),
    // ── Ad Library: собранные продающие рекламы (публично, вкладка в «Идеях») ──
    getAdLibrary: (params = {}) => {
        const q = new URLSearchParams();
        if (params.region)
            q.set('region', params.region);
        if (params.objective)
            q.set('objective', params.objective);
        if (params.category)
            q.set('category', params.category);
        q.set('limit', String(params.limit ?? 60));
        return call('GET', `/v1/public/ad-library?${q.toString()}`);
    },
    getAdLibraryCategories: () => call('GET', '/v1/public/ad-library/categories'),
    getAdBreakdown: (adId) => call('GET', `/v1/public/ad-library/breakdown?ad_id=${encodeURIComponent(adId)}`),
    // ── PAYG-паки видео-кредитов (top-up) ──
    getCreditPacks: () => call('GET', '/v1/public/packs'),
    initUnitpayPack: (pack) => call('POST', '/v1/payments/unitpay/pack-init', { pack }),
    initTinkoffPack: (pack) => call('POST', '/v1/payments/tinkoff/pack-init', { pack }),
    initRobokassaPack: (pack) => call('POST', '/v1/payments/robokassa/pack-init', { pack }),
    getPaymentsConfig: () => call('GET', '/v1/payments/config'),
    listPresets: () => call('GET', '/v1/webapp/presets'),
    createPreset: (body) => call('POST', '/v1/webapp/presets', body),
    patchPreset: (id, body) => call('PATCH', `/v1/webapp/presets/${id}`, body),
    deletePreset: (id) => call('DELETE', `/v1/webapp/presets/${id}`),
    // Скилы-«типы генераций»: системные + пользовательские (раздел «Скилы», чип в чате).
    listSkills: () => call('GET', '/v1/webapp/skills'),
    createSkill: (body) => call('POST', '/v1/webapp/skills', body),
    patchSkill: (id, body) => call('PATCH', `/v1/webapp/skills/${id}`, body),
    deleteSkill: (id) => call('DELETE', `/v1/webapp/skills/${id}`),
    shareSkill: (id) => call('POST', `/v1/webapp/skills/${id}/share`),
    unshareSkill: (id) => call('DELETE', `/v1/webapp/skills/${id}/share`),
    promoteSkillToMarketplace: (id, category) => call('POST', `/v1/webapp/skills/${id}/marketplace`, { category }),
    demoteSkillFromMarketplace: (id) => call('DELETE', `/v1/webapp/skills/${id}/marketplace`),
    importSkill: (token) => call('POST', '/v1/webapp/skills/import', { token }),
    // Публичное превью расшаренного скила (страница /s/<token>, работает без JWT).
    getPublicSkill: (token) => call('GET', `/v1/public/skills/${token}`),
    getPublicSkills: () => call('GET', '/v1/public/skills'),
    // AI-черновик пресета из понравившегося кадра (предпросмотр перед созданием).
    draftPresetFromGeneration: (generation_id) => call('POST', '/v1/webapp/presets/draft-from-generation', { generation_id }),
    // Пакетная обработка по пресету: до 10 входов → 10 отдельных генераций.
    batchGenerate: (body) => call('POST', '/v1/webapp/generations/batch', body),
    listPresetAssets: (id) => call('GET', `/v1/webapp/presets/${id}/assets`),
    async uploadPresetAsset(id, file) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE}/v1/webapp/presets/${id}/assets`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (!res.ok)
            throw new Error(`asset upload → ${res.status}`);
        return res.json();
    },
    deletePresetAsset: (presetId, assetId) => call('DELETE', `/v1/webapp/presets/${presetId}/assets/${assetId}`),
    materializePresetRefs: (id) => call('POST', `/v1/webapp/presets/${id}/assets/materialize`),
    assignGenerationPreset: (genId, presetId) => call('PATCH', `/v1/webapp/generations/${genId}/preset`, { preset_id: presetId }),
    createGeneration: (body) => call('POST', '/v1/webapp/generations', body),
    // Re-edit: правка уже готовой генерации инструкцией (img2img на бэке, Task 2).
    editGeneration: (genId, instruction) => call('POST', `/v1/webapp/generations/${genId}/edit`, {
        instruction,
    }),
    sendAssistantMessage: (text, refPaths = [], refGenIds = []) => call('POST', '/v1/webapp/assistant/message', {
        text,
        ref_paths: refPaths,
        ref_gen_ids: refGenIds,
    }),
    confirmAssistantMessage: (id) => call('POST', `/v1/webapp/assistant/messages/${id}/confirm`),
    listAssistantHistory: (limit = 30) => call('GET', `/v1/webapp/assistant/history?limit=${limit}`),
    frameSuggest: (generation_id, context = '') => call('POST', '/v1/webapp/assistant/frame-suggest', { generation_id, context }),
    // ── Scenix AI-Canvas persistence ──
    listCanvasProjects: () => call('GET', '/v1/canvas/projects'),
    getCanvasProject: (id) => call('GET', `/v1/canvas/projects/${id}`),
    createCanvasProject: (body) => call('POST', '/v1/canvas/projects', body),
    patchCanvasProject: (id, body) => call('PATCH', `/v1/canvas/projects/${id}`, body),
    deleteCanvasProject: (id) => call('DELETE', `/v1/canvas/projects/${id}`),
    createCanvas: (projectId, body) => call('POST', `/v1/canvas/projects/${projectId}/canvases`, body),
    getCanvas: (id) => call('GET', `/v1/canvas/canvases/${id}`),
    saveCanvas: (id, body) => call('PATCH', `/v1/canvas/canvases/${id}`, body),
    deleteCanvas: (id) => call('DELETE', `/v1/canvas/canvases/${id}`),
    // ── Курирование витрины «Примеры» (admin-only на бэке) ──
    publishShowcase: (canvasId, body) => call('POST', `/v1/canvas/canvases/${canvasId}/publish-showcase`, body),
    unpublishShowcase: (canvasId) => call('POST', `/v1/canvas/canvases/${canvasId}/unpublish-showcase`),
    listShowcaseCategories: () => call('GET', '/v1/admin/showcase/categories'),
    createShowcaseCategory: (label) => call('POST', '/v1/admin/showcase/categories', { label }),
    renameShowcaseCategory: (id, label) => call('PATCH', `/v1/admin/showcase/categories/${id}`, { label }),
    deleteShowcaseCategory: (id) => call('DELETE', `/v1/admin/showcase/categories/${id}`),
    reorderShowcaseCategories: (ids) => call('POST', '/v1/admin/showcase/categories/reorder', { ids }),
    reorderShowcaseCards: (categoryKey, canvasIds) => call('POST', '/v1/admin/showcase/reorder', { category_key: categoryKey, canvas_ids: canvasIds }),
    moveShowcaseCard: (canvasId, categoryKey) => call('POST', `/v1/admin/showcase/card/${canvasId}/move`, { category_key: categoryKey }),
    // ── Админ-каталог скилов: папки + курирование ──
    listSkillCategories: () => call('GET', '/v1/admin/skills/categories'),
    listSkillCatalog: () => call('GET', '/v1/admin/skills/catalog'),
    createSkillCategory: (label) => call('POST', '/v1/admin/skills/categories', { label }),
    renameSkillCategory: (id, label) => call('PATCH', `/v1/admin/skills/categories/${id}`, { label }),
    deleteSkillCategory: (id) => call('DELETE', `/v1/admin/skills/categories/${id}`),
    reorderSkillCategories: (ids) => call('POST', '/v1/admin/skills/categories/reorder', { ids }),
    patchSkillCuration: (name, patch) => call('PATCH', `/v1/admin/skills/${encodeURIComponent(name)}`, patch),
    reorderSkills: (category_key, names) => call('POST', '/v1/admin/skills/reorder', { category_key, names }),
    // ── «Режим Маркетинг» (Living Brief) ──
    // profileId — фильтр по компании SMM-хаба (CompanyView, таб «Кампании»); null/undefined → все кампании юзера.
    listCampaigns: (profileId) => {
        const q = profileId != null ? `?profile_id=${profileId}` : '';
        return call('GET', `/v1/webapp/campaigns${q}`);
    },
    getCampaign: (id) => call('GET', `/v1/webapp/campaigns/${id}`),
    createCampaign: (body) => call('POST', '/v1/webapp/campaigns', body),
    patchCampaign: (id, body) => call('PATCH', `/v1/webapp/campaigns/${id}`, body),
    runCampaign: (id) => call('POST', `/v1/webapp/campaigns/${id}/run`),
    // Точечные АТОМАРНЫЕ правки doc (текст / url-реф кадра) — не затирают весь
    // документ при конкурентной авто-генерации (бэкенд применяет под FOR UPDATE).
    campaignDocOps: (id, ops) => call('POST', `/v1/webapp/campaigns/${id}/doc-ops`, { ops }),
    // Засеять проект Студии из утверждённой раскадровки (кадр → видео-клип).
    campaignToStudio: (id) => call('POST', `/v1/webapp/campaigns/${id}/to-studio`),
    deleteCampaign: (id) => call('DELETE', `/v1/webapp/campaigns/${id}`),
    // ── SMM-пивот: компании → каналы → контент-план → посты (backend smm.py) ──
    listCompanies: () => call('GET', '/v1/webapp/companies'),
    createCompany: (body) => call('POST', '/v1/webapp/companies', body),
    getCompany: (id) => call('GET', `/v1/webapp/companies/${id}`),
    patchCompany: (id, body) => call('PATCH', `/v1/webapp/companies/${id}`, body),
    deleteCompany: (id) => call('DELETE', `/v1/webapp/companies/${id}`),
    listChannels: (companyId) => call('GET', `/v1/webapp/companies/${companyId}/channels`),
    createChannel: (companyId, body) => call('POST', `/v1/webapp/companies/${companyId}/channels`, body),
    deleteChannel: (id) => call('DELETE', `/v1/webapp/channels/${id}`),
    getPlan: (companyId) => call('GET', `/v1/webapp/companies/${companyId}/plan`),
    patchPlan: (companyId, body) => call('PATCH', `/v1/webapp/companies/${companyId}/plan`, body),
    // Ф1: AI-черновик контент-стратегии. Синхронный вызов; 402 no_ai_credits,
    // 409 generation_in_progress, 502 strategy_suggest_failed.
    suggestStrategy: (companyId) => call('POST', `/v1/webapp/companies/${companyId}/strategy/suggest`),
    // AI-генерация постов плана. Реальный режим — фон: posts в ответе пуст,
    // фронт добирает поллингом listPosts. 402 no_ai_credits, 409 generation_in_progress.
    generatePlan: (companyId) => call('POST', `/v1/webapp/companies/${companyId}/plan/generate`),
    listPosts: (companyId, status) => call('GET', `/v1/webapp/companies/${companyId}/posts${status ? `?status=${encodeURIComponent(status)}` : ''}`),
    // Ручной пост студии (напр. «В план» из вкладки «Идеи»): без scheduled_at
    // бэкенд сам ставит завтра 12:00 таймзоны компании. Лимит 10 черновиков → 429.
    createPost: (companyId, body) => call('POST', `/v1/webapp/companies/${companyId}/posts`, body),
    patchPost: (id, body) => call('PATCH', `/v1/webapp/posts/${id}`, body),
    // Продюсер поста: текст (+фото по visual_idea). regen=true — перегенерация
    // текста (лимит 5 → 429). 402 no_ai_credits, 409 not_regenerable/produce_in_progress.
    producePost: (id, regen = false) => call('POST', `/v1/webapp/posts/${id}/produce`, { regen }),
    // Решение по посту (та же семантика, что кнопки ✅/🔄/❌ в боте):
    // ok → approved, no → rejected, regen → перегенерация текста (пост остаётся ready).
    decidePost: (id, action) => call('POST', `/v1/webapp/posts/${id}/decision`, { action }),
    // Маркетолог-чат компании (Task 9/10): история на клиенте, messages целиком
    // каждый запрос. 503 marketer_busy — агент занят другим запросом.
    marketerChat: (companyId, messages) => call('POST', `/v1/webapp/companies/${companyId}/marketer/chat`, { messages }),
    // Готовое видео поста (реел/UGC/моушн) — multipart, паттерн uploadBrandMedia.
    async uploadPostVideo(postId, file) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE}/v1/webapp/posts/${postId}/video`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (!res.ok) {
            const text = await res.text();
            throw new Error(`video upload → ${res.status}: ${text.slice(0, 200)}`);
        }
        return res.json();
    },
    // ── Фотобанк реального контента клиента (Фаза 1, smm.py) ──
    listBrandMedia: (companyId, category) => call('GET', `/v1/webapp/companies/${companyId}/media${category ? `?category=${encodeURIComponent(category)}` : ''}`),
    suggestBrandMedia: (companyId, hint) => call('GET', `/v1/webapp/companies/${companyId}/media/suggest?hint=${encodeURIComponent(hint)}`),
    // Загрузка фото в банк — multipart (файл + описание + категория). Vision-AI
    // размечает фон-таском: analyzed придёт true при следующем listBrandMedia.
    async uploadBrandMedia(companyId, file, opts = {}) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        if (opts.title)
            fd.append('title', opts.title);
        if (opts.category)
            fd.append('category', opts.category);
        const res = await fetch(`${API_BASE}/v1/webapp/companies/${companyId}/media`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (!res.ok) {
            const text = await res.text();
            throw new Error(`media upload → ${res.status}: ${text.slice(0, 200)}`);
        }
        return res.json();
    },
    // ── Рефы бренда (лицо/товар/лого) — business_profiles.refs, вкладка «Бренд» ──
    // Оба эндпоинта возвращают полный CompanyOut с обновлённым массивом refs.
    async uploadCompanyRef(companyId, file, kind, title) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        if (title)
            fd.append('title', title);
        const res = await fetch(`${API_BASE}/v1/webapp/companies/${companyId}/refs`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (!res.ok) {
            const text = await res.text();
            throw new Error(`ref upload → ${res.status}: ${text.slice(0, 200)}`);
        }
        return res.json();
    },
    deleteCompanyRef: (companyId, refId) => call('DELETE', `/v1/webapp/companies/${companyId}/refs/${encodeURIComponent(refId)}`),
    getAiBalance: () => call('GET', '/v1/webapp/ai-credits'),
    getAiPacks: () => call('GET', '/v1/public/ai-packs'),
    // Покупка AI-пака: те же pack-init'ы, что видео-паки, но kind:"ai"
    // (paddle — свой checkout-эндпоинт с checkout_url вместо pay_url).
    initAiPack: (provider, pack) => provider === 'paddle'
        ? call('POST', '/v1/payments/paddle/pack-checkout', { pack, kind: 'ai' })
        : call('POST', `/v1/payments/${provider}/pack-init`, { pack, kind: 'ai' }),
    // ── Видео (бета, gated by access) ──
    getVideoBalance: () => call('GET', '/v1/webapp/video/balance'),
    getVideoModels: () => call('GET', '/v1/webapp/video/models'),
    listVideoGenerations: () => call('GET', '/v1/webapp/video/generations?limit=50'),
    getVideoGeneration: (id) => call('GET', `/v1/webapp/video/generations/${id}`),
    createVideo: (body) => call('POST', '/v1/webapp/video/generations', body),
    // ── Аудио (генерация треков) ──
    createAudio: (body) => call('POST', '/v1/webapp/audio/generations', body),
    listAudioGenerations: () => call('GET', '/v1/webapp/audio/generations'),
    getAudioGeneration: (id) => call('GET', `/v1/webapp/audio/generations/${id}`),
    getAudioBalance: () => call('GET', '/v1/webapp/audio/balance'),
    trackEvent: (event, meta) => call('POST', '/v1/webapp/events', { event, meta: meta ?? {} }).catch(() => ({ ok: false })),
    async uploadReference(file) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE}/v1/webapp/uploads`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (!res.ok) {
            const text = await res.text();
            throw new Error(`upload → ${res.status}: ${text.slice(0, 200)}`);
        }
        return res.json();
    },
    listRefLibrary: (section, opts = {}) => {
        const q = new URLSearchParams({ section, limit: String(opts.limit ?? 30) });
        if (opts.offset)
            q.set('offset', String(opts.offset));
        return call('GET', `/v1/webapp/library?${q}`);
    },
    attachLibraryItem: (id) => call('POST', '/v1/webapp/library/attach', { id }),
    saveLibraryItem: (id, target, name) => call('POST', '/v1/webapp/library/save', { id, target, name }),
    // ── Коллекции (единый CRUD разделов + элементов) ──
    listCollections: () => call('GET', '/v1/webapp/library/collections'),
    createCollection: (name, emoji) => call('POST', '/v1/webapp/library/collections', { name, emoji }),
    updateCollection: (id, patch) => call('PATCH', `/v1/webapp/library/collections/${encodeURIComponent(id)}`, patch),
    deleteCollection: (id) => call('DELETE', `/v1/webapp/library/collections/${encodeURIComponent(id)}`),
    listCollectionItems: (id, opts = {}) => {
        const q = new URLSearchParams({ limit: String(opts.limit ?? 30) });
        if (opts.offset)
            q.set('offset', String(opts.offset));
        return call('GET', `/v1/webapp/library/collections/${encodeURIComponent(id)}/items?${q}`);
    },
    addCollectionItem: (id, source_id, name) => call('POST', `/v1/webapp/library/collections/${encodeURIComponent(id)}/items`, { source_id, name }),
    removeCollectionItem: (id, itemId) => call('DELETE', `/v1/webapp/library/collections/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`),
    // ── Аватары (HeyGen, admin-only beta) ──
    listAvatars: () => call('GET', '/v1/webapp/avatars'),
    createAvatar: (body) => call('POST', '/v1/webapp/avatars', body),
    generateAvatarVideo: (body) => call('POST', '/v1/webapp/avatars/video', body),
    listStockPhotos: () => call('GET', '/v1/webapp/avatars/stock-photos'),
    listAvatarVoices: () => call('GET', '/v1/webapp/avatars/voices'),
    async uploadAvatarPhoto(file) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE}/v1/webapp/avatars/upload-photo`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (res.status === 501)
            throw new Error('PRO_REQUIRED');
        if (!res.ok)
            throw new Error(`upload → ${res.status}`);
        return res.json();
    },
    async cloneVoice(file, name) {
        const token = getAuthToken();
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', name);
        const res = await fetch(`${API_BASE}/v1/webapp/avatars/voice-clone`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (!res.ok)
            throw new Error(`voice-clone → ${res.status}`);
        return res.json();
    },
    voiceCloneStatus: (jobId) => call('GET', `/v1/webapp/avatars/voice-clone/status?job_id=${encodeURIComponent(jobId)}`),
    listClonedVoices: () => call('GET', '/v1/webapp/avatars/cloned-voices'),
    // ── Developer API-ключи ──
    getApiKeys: () => call('GET', '/v1/webapp/developer/api-keys'),
    createApiKey: (name) => call('POST', '/v1/webapp/developer/api-keys', { name }),
    revokeApiKey: (id) => call('DELETE', `/v1/webapp/developer/api-keys/${id}`),
    getApiKeyUsage: () => call('GET', '/v1/webapp/developer/api-keys/usage'),
    // ── Голосовой ввод (Deepgram STT, batch) ──
    // Шлём сырой аудио-blob (webm/opus, mp4/aac…) multipart'ом; backend
    // проксирует в Deepgram и возвращает распознанный текст. mimeType blob'а —
    // это его .type (MediaRecorder проставляет реальный контейнер).
    async transcribeAudio(blob, language) {
        const token = getAuthToken();
        const fd = new FormData();
        const ext = (blob.type.split('/')[1] || 'webm').split(';')[0];
        fd.append('file', blob, `voice.${ext}`);
        // Явный язык (локаль UI) точнее и быстрее автоопределения на стороне Deepgram.
        if (language)
            fd.append('language', language);
        const res = await fetch(`${API_BASE}/v1/webapp/stt`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
        });
        if (res.status === 503)
            throw new Error('STT_UNAVAILABLE');
        if (!res.ok)
            throw new Error(`stt → ${res.status}`);
        return res.json();
    },
};
